<script setup lang="ts">
import {computed, ref} from 'vue'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useAccessStore } from '@/stores/access'

import {formatTime} from '@/utils/datetime'
import { MoreHorizontal } from 'lucide-vue-next'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { can } from '@/composables/useCan'

import type { OrgMember } from '@/types'
import MembersListEditDialog from './MembersListEditDialog.vue'


const orgStore = useOrgStore()


const userStore = useUserStore()


const accessStore = useAccessStore()
const settings = computed(() => userStore.settings)
const org = computed(() => orgStore.current)
const props = defineProps<{
  member: OrgMember,
}>()

const isOwner = computed(() => accessStore.ownerMemberId === props.member.member_id)
const createdAt = computed(() => formatTime(props.member.create_dttm, settings.value.tz, 'D MMMM YYYY'))
const editOpen = ref(false)
const canUpdate = computed(() => can('org_member', 'update', { orgId: org.value.organization_id, allowSuperUser: true }))
const canDelete = computed(() => can('org_member', 'delete', { orgId: org.value.organization_id, allowSuperUser: true }))

const remove = async (ev: any) => {
  await orgStore.deleteMember({
    orgId: org.value.organization_id,
    orgMemberId: props.member.organization_member_id
  })
}

</script>

<template>
  <MembersListEditDialog
    v-if="canUpdate"
    :member="member"
    :org="org"
    :open="editOpen"
    @update:open="editOpen = $event"
  />

  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" class="w-8 h-8 p-0">
        <span class="sr-only">Open</span>
        <MoreHorizontal class="w-4 h-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem>Member since: {{createdAt}}</DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        v-if="canUpdate"
        @click="editOpen = true">Edit</DropdownMenuItem>
      <template v-if="canDelete">
        <DropdownMenuItem v-if="isOwner" disabled class="flex-col items-start" data-testid="owner-remove-blocked">
          <span>Delete</span>
          <span class="text-xs text-muted-foreground">Transfer ownership in General settings first</span>
        </DropdownMenuItem>
        <DropdownMenuItem v-else @click="remove" class="text-red-500">Delete</DropdownMenuItem>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
